import * as XLSX from 'xlsx';
import { t } from "i18next";

class DeviceLogApi {
    static RECORD_SIZE = 8; // 8 bytes

    static MAIN_STATES = [
        { intCode: 0, charCode: "H" }, // MAIN_STATE_HEAT
        { intCode: 1, charCode: "S" }, // MAIN_STATE_SLEEP
        { intCode: 2, charCode: "C" }, // MAIN_STATE_CHARGE
        { intCode: 3, charCode: "P" }, // MAIN_STATE_POW_BANK
        { intCode: 4, charCode: "D" }, // MAIN_STATE_DRYING
        { intCode: 5, charCode: "E" }, // MAIN_STATE_ERROR
        { intCode: 6, charCode: "L" }, // MAIN_STATE_BAT_LOW
        { intCode: 7, charCode: "F" }, // MAIN_STATE_BAT_FULL
        { intCode: 8, charCode: "G" }, // MAIN_STATE_SHUTTING_DOWN
    ];

    static CONTROL_MODES = [
        { intCode: 0, charCode: "I" }, // CONTROL_MODE_INIT
        { intCode: 1, charCode: "M" }, // CONTROL_MODE_MAX
        { intCode: 2, charCode: "D" }, // CONTROL_MODE_MED
        { intCode: 3, charCode: "N" }, // CONTROL_MODE_MIN
        { intCode: 4, charCode: "O" }, // CONTROL_MODE_OFF
        { intCode: 5, charCode: "S" }, // CONTROL_MODE_SMART
        { intCode: 6, charCode: "H" }, // CONTROL_MODE_SHUTDOWN
    ];

    static HEATER_STATES = [
        { intCode: 0, charCode: "0" }, // HEATER_STATE_OFF
        { intCode: 1, charCode: "1" }, // HEATER_STATE_MIN_MIN
        { intCode: 2, charCode: "2" }, // HEATER_STATE_MIN
        { intCode: 3, charCode: "3" }, // HEATER_STATE_MED
        { intCode: 4, charCode: "4" }, // HEATER_STATE_MAX
    ];

    static CHARGE_PLUGGED_STATES = [
        { intCode: 0, charCode: "U" },
        { intCode: 1, charCode: "P" },
    ];

    static CHARGE_END_STATES = [
        { intCode: 0, charCode: "N" },
        { intCode: 1, charCode: "E" },
    ];

    static intCodeToCharCode(codes, intCode) {
        return codes.find(c => c.intCode === intCode)?.charCode ?? "?";
    }

    static getMainStateText(mainStateIntCode) {
        switch (mainStateIntCode) {
            case 0: return t("koo.state.heat");
            case 1: return t("koo.state.sleep");
            case 2: return t("koo.state.charge");
            case 3: return t("koo.state.pow_bank");
            case 4: return t("koo.state.drying");
            case 5: return t("koo.state.error");
            case 6: return t("koo.state.bat_low");
            case 7: return t("koo.state.bat_full");
            case 8: return t("koo.state.shutting_down");
            default: return "?";
        }
    }

    static getControlModeText(controlModeIntCode) {
        switch (controlModeIntCode) {
            case 0: return t("koo.mode.init");
            case 1: return t("koo.mode.pwm_100");
            case 2: return t("koo.mode.pwm_50");
            case 3: return t("koo.mode.pwm_25");
            case 4: return t("koo.mode.pwm_0");
            case 5: return t("koo.mode.smart_heat");
            case 6: return t("koo.mode.shutdown");
            default: return "?";
        }
    }

    static getHeaterStateText(heaterStateIntCode) {
        switch (heaterStateIntCode) {
            case 0: return t("koo.heat.heat_0");
            case 1: return t("koo.heat.min_min_avg");
            case 2: return t("koo.heat.min_avg");
            case 3: return t("koo.heat.med_avg");
            case 4: return t("koo.heat.max_avg");
            default: return "?";
        }
    }

    static getChargePluggedStateText(chargePluggedStateIntCode) {
        switch (chargePluggedStateIntCode) {
            case 0: return t("koo.charge.unplugged");
            case 1: return t("koo.charge.plugged");
            default: return "?";
        }
    }

    static getChargeEndStateText(chargeEndStateIntCode) {
        switch (chargeEndStateIntCode) {
            case 0: return t("koo.charge_end.no");
            case 1: return t("koo.charge_end.yes");
            default: return "?";
        }
    }

    static prettyRecord(record) {
        return {
            Time_sec: record.time2s * 2,
            MainState: DeviceLogApi.getMainStateText(record.mainState),
            ControlMode: DeviceLogApi.getControlModeText(record.controlMode),
            HeaterState: DeviceLogApi.getHeaterStateText(record.heaterState),
            ChargePlugged: DeviceLogApi.getChargePluggedStateText(record.chargePlugged),
            ChargeEnd: DeviceLogApi.getChargeEndStateText(record.chargeEnd),
            Voltage_volt: record.voltage,
            Current_amp: record.current,
            McuTemp_deg: record.mcuTemp,
            SensTemp_deg: record.sensTemp,
        };
    }

    constructor(buf) {
        // Handle both ArrayBuffer and Uint8Array
        if (buf instanceof ArrayBuffer) {
            this.buf = new Uint8Array(buf);
        } else {
            this.buf = buf;
        }
    }

    recordCount() {
        return this.buf.byteLength / DeviceLogApi.RECORD_SIZE;
    }

    record(index) {
        // NOTE: data format is little-endian:
        // __attribute__ ((packed))
        // struct DataLogRecord {
        //     uint16_t time2s;
        //     uint16_t modeState;
        //     uint8_t voltage;
        //     int8_t current;
        //     uint8_t mcuTemp;
        //     uint8_t sensTemp;
        // };

        const offset = index * DeviceLogApi.RECORD_SIZE;
        const recordBuf = this.buf.slice(offset, offset + DeviceLogApi.RECORD_SIZE);

        const time2s = recordBuf[0] | (recordBuf[1] << 8);

        const modeState = recordBuf[2] | (recordBuf[3] << 8);
        let mainState = (modeState >> 8) & 0xF;
        let controlMode = (modeState >> 5) & 0x7;
        let heaterState = (modeState >> 2) & 0x7;
        let chargePlugged = (modeState >> 1) & 0x1;
        let chargeEnd = modeState & 0x1;

        let voltage = recordBuf[4];
        if (voltage === 0xFF) {
            voltage = null;
        } else {
            voltage = voltage * 0.02;
        }

        let current = recordBuf[5];
        if (current === 0x7F) {
            current = null;
        } else {
            // Convert from twos complement
            if ((current & 0x80) > 0) {
                current = current - 256;
            }
            current = current * 0.02;
        }

        let mcuTemp = recordBuf[6];
        if (mcuTemp === 0x7F) {
            mcuTemp = null;
        } else {
            if ((mcuTemp & 0x80) > 0) {
                mcuTemp = mcuTemp - 256;
            }
        }

        let sensTemp = recordBuf[7];
        if (sensTemp === 0x7F) {
            sensTemp = null;
        } else {
            if ((sensTemp & 0x80) > 0) {
                sensTemp = sensTemp - 256;
            }
        }

        return {
            time2s,
            mainState,
            controlMode,
            heaterState,
            chargePlugged,
            chargeEnd,
            voltage,
            current,
            mcuTemp,
            sensTemp
        };
    }

    records() {
        const recordCount = this.recordCount();
        const records = [];
        for (let i = 0; i < recordCount; i++) {
            records.push(this.record(i));
        }
        return records;
    }

    valuableRecords() {
        return this.records().filter(record => record.time2s !== 0xFFFF);
    }

    exportToXlsx({ pretty = false }) {
        let recs = this.valuableRecords();

        if (pretty) {
            recs = recs.map(DeviceLogApi.prettyRecord);
        }

        // Create workbook
        const workbook = XLSX.utils.book_new();

        // Convert the records to a worksheet with column headers
        const worksheet = XLSX.utils.json_to_sheet(recs);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Device Logs');

        // Generate the XLSX file
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

        return excelBuffer;
    }
};

export default DeviceLogApi;
